import { cur } from '@/lib/currency';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, RefreshCw, Search, Trash2, ShieldAlert, CheckCircle2, Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtSR = (v) => `${cur()} ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * iter395g — Phantom Supplier Payments cleanup card.
 *
 * Wires the maintenance endpoint
 *   GET  /api/maintenance/phantom-supplier-payments
 *   POST /api/maintenance/phantom-supplier-payments/clean?confirm=true
 * into a guided UI:
 *   1. Click "Run preview" → shows count, breakdown by mode, sample table
 *   2. User ticks "I've reviewed the preview" + clicks "Delete + Recalculate"
 *   3. Result toast + auto-refresh of preview to confirm 0 remain
 *
 * Why exists: cash/bank purchases were being mis-recorded as
 * "Settlements of old credit bills" because two old code paths dual-
 * wrote a phantom supplier_payment alongside each expense. Code paths
 * fixed in iter395f; this UI cleans the historical data.
 */
export default function PhantomSupplierPaymentsCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      // iter395k — bumped sample from 20 → 200 so users see a meaningful
      // chunk of the preview without having to export. The export button
      // covers the "every single row" use-case.
      const { data } = await api.get('/maintenance/phantom-supplier-payments', { params: { sample: 200 } });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run preview');
    } finally { setLoading(false); }
  };

  // iter395k — Excel export of all phantom rows so users can tally the
  // amounts against their bank statements before deleting.
  const exportExcel = async () => {
    try {
      const { data, headers } = await api.get('/maintenance/phantom-supplier-payments/export', { responseType: 'blob' });
      const cd = headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `phantom_supplier_payments_${Date.now()}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel export downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to export Excel');
    }
  };

  const runClean = async () => {
    if (!preview || !acknowledged) return;
    if (preview.match_count === 0) {
      toast.info('Nothing to delete — preview shows 0 phantom rows.');
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.post('/maintenance/phantom-supplier-payments/clean?confirm=true');
      setLastResult(data);
      toast.success(`Deleted ${data.deleted} phantom rows • Recalculated ${data.recalculated_suppliers} suppliers`);
      // Re-run preview so the user sees the count drop to 0.
      await runPreview();
      setAcknowledged(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clean phantom rows');
    } finally { setDeleting(false); }
  };

  const totalAmount = (preview?.by_payment_mode || []).reduce((sum, r) => sum + (r.total || 0), 0);

  return (
    <Card className="border-border" data-testid="phantom-payments-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <ShieldAlert className="text-rose-500" size={18} />
          Clean Phantom Supplier Payments
        </CardTitle>
        <CardDescription className="leading-relaxed">
          Removes incorrectly-classified <code className="bg-muted px-1 rounded text-[11px]">supplier_payments</code> rows that were created
          by an old bug — cash/bank purchase bills used to dual-write a phantom &quot;settlement&quot; record alongside the expense, which
          inflated the &quot;Settlements of old credit bills&quot; figure on Branch Summary and Combined Cash Outflow reports.
          Match heuristic: <code className="bg-muted px-1 rounded text-[11px]">notes</code> starts with <code className="bg-muted px-1 rounded text-[11px]">&quot;Bill: &quot;</code> and payment_mode is cash/bank.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Step 1 — Preview */}
        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/50 text-blue-800 dark:text-blue-200">Step 1</Badge>
            <span className="text-sm font-semibold">Preview what will be deleted</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runPreview}
              disabled={loading}
              data-testid="phantom-preview-btn"
            >
              {loading ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Search size={14} className="mr-2" />}
              {preview ? 'Refresh preview' : 'Run preview'}
            </Button>
            {preview && preview.match_count > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportExcel}
                data-testid="phantom-export-btn"
              >
                <Download size={14} className="mr-2" />
                Export to Excel ({preview.match_count} rows)
              </Button>
            )}
          </div>

          {preview && (
            <div className="space-y-3" data-testid="phantom-preview-results">
              <div className={`rounded-lg p-3 text-sm ${preview.match_count === 0 ? 'bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200' : 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-amber-900 dark:text-amber-200'}`}>
                {preview.match_count === 0 ? (
                  <span className="flex items-center gap-2"><CheckCircle2 size={16} /> No phantom rows in this tenant — nothing to clean.</span>
                ) : (
                  <span className="flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>
                      Found <b>{preview.match_count}</b> phantom row{preview.match_count === 1 ? '' : 's'} totalling <b>{fmtSR(totalAmount)}</b>{' '}
                      across <b>{preview.affected_supplier_count}</b> supplier{preview.affected_supplier_count === 1 ? '' : 's'}. Review the breakdown below before deleting.
                    </span>
                  </span>
                )}
              </div>

              {preview.by_payment_mode?.length > 0 && (
                <div>
                  <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Breakdown by payment mode</h4>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-[11px] uppercase">
                          <tr>
                            <th className="text-left p-2">Mode</th>
                            <th className="text-right p-2">Rows</th>
                            <th className="text-right p-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.by_payment_mode.map((r) => (
                            <tr key={r.payment_mode} className="border-t border-border/50">
                              <td className="p-2 capitalize font-medium">{r.payment_mode}</td>
                              <td className="p-2 text-right tabular-nums">{r.count}</td>
                              <td className="p-2 text-right tabular-nums font-mono">{fmtSR(r.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* iter395k — Branch breakdown so users see which branch
                  contributed how much. Matches the user's screenshot
                  request for a Branch column / total. */}
              {preview.by_branch?.length > 0 && (
                <div>
                  <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Breakdown by branch</h4>
                  <div className="rounded-lg border border-border overflow-hidden max-h-64 overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="phantom-by-branch-table">
                        <thead className="bg-muted/50 text-[11px] uppercase sticky top-0">
                          <tr>
                            <th className="text-left p-2">Branch</th>
                            <th className="text-right p-2">Rows</th>
                            <th className="text-right p-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.by_branch.map((r) => (
                            <tr key={r.branch_id || 'no-branch'} className="border-t border-border/50">
                              <td className="p-2 font-medium">{r.branch_name}</td>
                              <td className="p-2 text-right tabular-nums">{r.count}</td>
                              <td className="p-2 text-right tabular-nums font-mono">{fmtSR(r.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {preview.sample?.length > 0 && (
                <div>
                  <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Sample rows ({preview.sample.length} of {preview.match_count})</h4>
                  <div className="rounded-lg border border-border overflow-hidden max-h-96 overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="phantom-sample-table">
                        <thead className="bg-muted/50 text-[10px] uppercase sticky top-0">
                          <tr>
                            <th className="text-left p-1.5">Date</th>
                            <th className="text-left p-1.5">Branch</th>
                            <th className="text-left p-1.5">Supplier</th>
                            <th className="text-left p-1.5">Mode</th>
                            <th className="text-left p-1.5">Notes</th>
                            <th className="text-right p-1.5">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.sample.map((p) => (
                            <tr key={p.id} className="border-t border-border/50 hover:bg-muted/30">
                              <td className="p-1.5 text-xs">{(p.date || '').slice(0, 10)}</td>
                              <td className="p-1.5 text-xs">{p.branch_name || '-'}</td>
                              <td className="p-1.5 text-xs">{p.supplier_name || '-'}</td>
                              <td className="p-1.5 text-xs capitalize">{p.payment_mode}</td>
                              <td className="p-1.5 text-xs text-muted-foreground truncate max-w-[260px]" title={p.notes}>{p.notes}</td>
                              <td className="p-1.5 text-xs text-right font-mono tabular-nums">{fmtSR(p.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {preview.sample.length < preview.match_count && (
                    <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                      Showing first {preview.sample.length} rows. Click <b>Export to Excel</b> above to get all {preview.match_count} rows.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2 — Confirm + Delete */}
        {preview && preview.match_count > 0 && (
          <div className="rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/40 dark:bg-rose-950/10 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] bg-rose-100 dark:bg-rose-950/40 border-rose-300 dark:border-rose-900/60 text-rose-800 dark:text-rose-200">Step 2</Badge>
              <span className="text-sm font-semibold text-rose-900 dark:text-rose-200">Confirm and delete</span>
            </div>
            <div className="text-xs text-rose-800 dark:text-rose-200/80 leading-relaxed">
              This permanently removes the {preview.match_count} matched <code className="bg-rose-100 dark:bg-rose-950/40 px-1 rounded">supplier_payments</code> row{preview.match_count === 1 ? '' : 's'} and recalculates each affected supplier&apos;s <code className="bg-rose-100 dark:bg-rose-950/40 px-1 rounded">current_credit</code>.
              The original expense records are <b>not</b> touched. Make sure you&apos;ve taken a backup of your data before running this on production.
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={setAcknowledged}
                className="mt-0.5"
                data-testid="phantom-confirm-checkbox"
              />
              <span className="text-rose-900 dark:text-rose-100">I have reviewed the preview above and understand this will permanently delete the matched rows.</span>
            </label>
            <Button
              variant="destructive"
              size="sm"
              onClick={runClean}
              disabled={!acknowledged || deleting}
              data-testid="phantom-delete-btn"
            >
              {deleting ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
              Delete {preview.match_count} row{preview.match_count === 1 ? '' : 's'} + recalculate suppliers
            </Button>
          </div>
        )}

        {/* Last run result — sticky at the bottom so the user has a record */}
        {lastResult && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-3 text-sm text-emerald-900 dark:text-emerald-200" data-testid="phantom-last-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} />
              <span><b>{lastResult.deleted}</b> rows deleted • <b>{lastResult.recalculated_suppliers}</b> suppliers recalculated.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
